import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Award, Clock, HelpCircle, CheckCircle2, Lock, PlayCircle, BookOpen } from "lucide-react";

const ctaLabelFor = (isStaff, enr, progress) => {
  if (!isStaff) return "Preview course";
  if (enr?.status === "completed") return "Review course";
  return progress > 0 ? "Continue learning" : "Start course";
};
const LessonNumber = ({ done, locked, index }) => {
  if (done) return <CheckCircle2 className="h-5 w-5 text-rag-green" />;
  if (locked) return <Lock className="h-4 w-4 text-muted-foreground" />;
  return index + 1;
};
const LessonStatus = ({ done, locked }) => {
  if (done) return <Badge variant="outline" className="text-rag-green border-rag-green/40">Passed</Badge>;
  return <PlayCircle className={`h-6 w-6 transition-transform ${locked ? "text-muted-foreground/40" : "text-brand group-hover:scale-110 group-hover:text-coral"}`} />;
};

export default function CourseOverview() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isStaff = user.role === "staff";
  const [course, setCourse] = useState(null);
  const [enr, setEnr] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/courses/${id}`);
    setCourse(data);
    if (isStaff) {
      const e = await api.get("/enrollments/me");
      setEnr(e.data.find((x) => x.course_id === id) || null);
    }
  }, [id, isStaff]);
  useEffect(() => { load(); }, [load]);

  if (!course) return <div className="grid place-items-center py-24 text-muted-foreground">Loading…</div>;

  const lessons = course.lessons || [];
  const completed = new Set(enr?.completed_lessons || []);
  const totalQ = lessons.reduce((n, l) => n + (l.quiz?.length || 0), 0);
  const totalMin = lessons.reduce((n, l) => n + (l.duration_min || 0), 0);
  const progress = enr?.progress || 0;
  const firstIncomplete = lessons.findIndex((l) => !completed.has(l.id));
  const ctaLabel = ctaLabelFor(isStaff, enr, progress);
  const isLocked = (i) => isStaff && i > 0 && !completed.has(lessons[i - 1]?.id);

  const go = () => {
    const target = firstIncomplete >= 0 ? firstIncomplete : 0;
    nav(`/app/courses/${id}/learn`, { state: { start: target } });
  };
  const openModule = (i) => {
    if (isLocked(i)) { toast.info("Complete the previous module first."); return; }
    nav(`/app/courses/${id}/learn`, { state: { start: i } });
  };
  const onRowKey = (e, i) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openModule(i); } };

  return (
    <div>
      <button onClick={() => nav("/app/courses")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-brand mb-4"><ArrowLeft className="h-4 w-4" />Back to courses</button>

      <div className="bg-brand text-white rounded-2xl p-8 relative overflow-hidden" data-testid="course-overview-hero">
        <Badge className="bg-white/15 text-white hover:bg-white/15 mb-3">{course.category}</Badge>
        <h1 className="text-3xl font-bold tracking-tight">{course.title}</h1>
        <p className="text-white/80 mt-3 max-w-2xl leading-relaxed">{course.description}</p>
        <div className="flex flex-wrap gap-6 mt-6 text-sm">
          <span className="flex items-center gap-2" data-testid="ov-modules"><BookOpen className="h-4 w-4" />{lessons.length} modules</span>
          <span className="flex items-center gap-2" data-testid="ov-cpd"><Award className="h-4 w-4" />{course.cpd_hours} CPD hours</span>
          <span className="flex items-center gap-2"><HelpCircle className="h-4 w-4" />{totalQ} quiz questions</span>
          <span className="flex items-center gap-2"><Clock className="h-4 w-4" />~{totalMin} min</span>
        </div>
        {isStaff && enr && (
          <div className="mt-6 max-w-md">
            <div className="flex justify-between text-xs text-white/70 mb-1"><span>Your progress</span><span>{progress}%</span></div>
            <div className="h-2.5 bg-white/20 rounded-full overflow-hidden"><div className="h-full bg-coral rounded-full transition-all" style={{ width: `${progress}%` }} /></div>
          </div>
        )}
        <Button data-testid="course-cta-btn" onClick={go} className="mt-6 rounded-full bg-coral hover:bg-coral-hover text-white">{ctaLabel}</Button>
      </div>

      <h2 className="font-heading font-bold text-brand text-xl mt-8 mb-4">What you'll cover</h2>
      <div className="space-y-3">
        {lessons.map((l, i) => {
          const locked = isLocked(i);
          const done = completed.has(l.id);
          return (
            <div key={l.id} data-testid={`ov-module-${i}`} role="button" tabIndex={0} aria-disabled={locked} onClick={() => openModule(i)} onKeyDown={(e) => onRowKey(e, i)}
              className={`group bg-cream-card border rounded-xl p-4 flex items-center gap-4 outline-none transition-all ${locked ? "border-cream-deep opacity-70 cursor-not-allowed" : "border-cream-deep cursor-pointer hover:border-brand/50 hover:shadow-md hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-coral"}`}>
              <div className="h-10 w-10 rounded-full grid place-items-center flex-shrink-0 bg-brand-light text-brand font-bold">
                <LessonNumber done={done} locked={locked} index={i} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground truncate">{l.title}</p>
                <div className="flex items-center gap-4 text-xs text-muted-foreground mt-0.5">
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{l.duration_min} min</span>
                  {!!(l.quiz?.length) && <span className="flex items-center gap-1"><HelpCircle className="h-3 w-3" />{l.quiz.length}-question quiz</span>}
                </div>
              </div>
              <LessonStatus done={done} locked={locked} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
